import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Cpu, Vote, ShieldCheck, ScanSearch } from "lucide-react";
import { Reveal } from "./common";
import { TChapter, useLang, t } from "../i18n";
import { PATENTS } from "../data/cv";

const FEATURES = [
  { icon: Vote, title: "Digital Voter Empowerment", text: "Navigational AI that guides citizens through candidates, manifestos and polling data." },
  { icon: Cpu, title: "Big-Data Electoral Mapping", text: "ECI datasets + behavioural analytics to model voting patterns at constituency scale." },
  { icon: ScanSearch, title: "Candidate Scrutiny", text: "Profile-scanning device scoring politicians on record, promises and public perception." },
  { icon: ShieldCheck, title: "Patent Protected", text: "3 patents + 2 copyrights granted by the Government of India (2024–2026)." },
];

const Chunav = () => {
  const { lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section id="chunav" ref={ref} className="relative bg-void text-bone bg-grid-dark py-24 sm:py-36 overflow-hidden" data-testid="section-chunav">
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-crimson/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-saffron/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <TChapter dark num="04" k="c4" />

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div>
            <Reveal>
              <p className="text-bone/70 text-base sm:text-lg leading-relaxed mb-10">
                Born from my Ph.D. outcomes, CHUNAV turns election science into a working product: a device and
                platform that reads voter behaviour, scrutinises candidates and empowers citizens at the ballot box.
                One finding even led the <span className="text-bone font-semibold">Election Commission of India to rectify its own
                public database</span> after an RTI query from my research.
              </p>
            </Reveal>

            <div className="grid sm:grid-cols-2 gap-4 mb-10">
              {FEATURES.map((f, i) => {
                const Icon = f.icon;
                return (
                  <Reveal key={f.title} delay={i * 0.08} className="bg-card_dark border border-bone/10 rounded-2xl p-5 hover:border-saffron/40 transition-colors duration-300">
                    <Icon size={20} className="text-saffron mb-3" />
                    <h3 className="font-display font-semibold text-lg mb-1.5">{f.title}</h3>
                    <p className="text-sm text-bone/60 leading-relaxed">{f.text}</p>
                  </Reveal>
                );
              })}
            </div>

            <Reveal delay={0.15}>
              <a
                data-testid="prototype-bharatelect-link"
                href="https://electbharat.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-flex items-center gap-3 bg-crimson text-bone font-mono text-xs sm:text-sm uppercase tracking-[0.2em] px-8 py-5 rounded-full overflow-hidden"
              >
                <span className="absolute inset-0 bg-saffron translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
                <span className="relative">{t(lang, "chunav_cta")}</span>
                <span className="relative font-bold">electbharat.com</span>
                <ArrowUpRight size={16} className="relative group-hover:rotate-45 transition-transform duration-300" />
              </a>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/40">
                {t(lang, "chunav_caption")}
              </p>
            </Reveal>
          </div>

          <div className="space-y-6">
            <motion.div style={{ y: yImg }} className="relative">
              <div className="absolute -inset-3 bg-gradient-to-br from-crimson/40 via-saffron/30 to-cobalt/30 rounded-[2rem] blur-xl opacity-60" />
              <div className="relative rounded-[1.75rem] overflow-hidden border border-bone/15">
                <img
                  src="https://images.unsplash.com/photo-1674027444485-cec3da58eef4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                  alt="CHUNAV AI neural network visualization"
                  className="w-full h-64 sm:h-80 object-cover"
                  data-testid="chunav-hero-image"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-void via-void/70 to-transparent p-6 pt-16">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-saffron mb-1">Flagship Prototype</p>
                  <p className="font-display text-2xl font-bold">CHUNAV AI · electbharat.com</p>
                </div>
              </div>
            </motion.div>

            <div className="bg-card_dark border border-bone/10 rounded-2xl overflow-hidden" data-testid="patent-ledger">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50 px-6 pt-5 pb-3 border-b border-bone/10">
                IP Ledger — Govt. of India
              </p>
              {PATENTS.map((p, i) => (
                <Reveal key={p.no} delay={i * 0.05} className="flex items-start justify-between gap-4 px-6 py-4 border-b border-bone/5 last:border-0 hover:bg-card_dark/[0.03] transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-bone/90 leading-snug">{p.title}</p>
                    <p className="font-mono text-[10px] text-bone/40 mt-1">{p.no}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`font-mono text-[9px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full ${p.kind === "Patent" ? "bg-crimson/20 text-crimson" : "bg-saffron/20 text-saffron"}`}>
                      {p.kind}
                    </span>
                    <p className="font-mono text-[9px] text-emerald mt-1.5">{p.status}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Chunav;
